import { createSlice } from '@reduxjs/toolkit';

import { communicationApi } from 'api';
import { transformFiltersToQuery } from './utils/transformFilterToQuery';

export const senderSlice = createSlice({
    name: 'sender',
    initialState: {
        pending: false,
        savePending: false,
        error: null,
        senders: [],
        count: 0,
        total: 0,
        currentPage: 1,
        totalPages: 1,
        deletePending: false,
    },
    reducers: {
        start: (state) => {
            state.error = null;
            state.pending = true;
        },
        stop: (state) => {
            state.pending = false;
        },
        setError: (state, action) => {
            state.error = action.payload;
            state.pending = false;
        },
        setPending: (state, action) => {
            state.pending = action.payload;
        },
        setSenders: (state, action) => {
            state.pending = false;
            state.count = action.payload.count;
            state.total = action.payload.total;
            state.senders = action.payload.items;
            state.currentPage = action.payload.currentPage;
            state.totalPages = action.payload.totalPages;
        },
        setSavePending: (state, action) => {
            state.savePending = action.payload;
        },
        addSender: (state, action) => {
            state.pending = false;
            state.senders = [action.payload, ...state.senders];
            state.total = state.total + 1;
            if (state.totalPages === 0) {
                state.totalPages = 1;
            }
        },
        editSender: (state, action) => {
            state.pending = false;
            const index = state.senders.findIndex((s) => s.id === action.payload.id);
            if (index !== -1) {
                state.senders = [
                    ...state.senders.slice(0, index),
                    action.payload,
                    ...state.senders.slice(index + 1),
                ];
            }
        },
        deleteSender: (state, action) => {
            state.deletePending = false;
            const index = state.senders.findIndex((s) => s.id === action.payload.id);
            if (index !== -1) {
                state.senders = [...state.senders.slice(0, index), ...state.senders.slice(index + 1)];
            }
            state.total = state.total - 1;
            if (state.total === 0) {
                state.totalPages = 0;
            }
        },
        startDelete: (state) => {
            state.deletePending = true;
        },
        confirmSender: (state, action) => {
            const index = state.senders.findIndex((s) => s.id === action.payload.id);
            if (index !== -1) {
                const updated = { ...state.senders[index], verified: true, enabled: true };
                state.senders = [
                    ...state.senders.slice(0, index),
                    updated,
                    ...state.senders.slice(index + 1),
                ];
            }
        },
    },
});

export const {
    start,
    stop,
    setError,
    setSenders,
    setPending,
    setSavePending,
    addSender,
    editSender,
    deleteSender,
    startDelete,
    confirmSender,
} = senderSlice.actions;

export const getSenderPending = (state) => state.sender.pending;
export const getSenderError = (state) => state.sender.error;
export const getSenders = (state) => state.sender.senders;
export const getSavePending = (state) => state.sender.savePending;
export const getSendersTotal = (state) => state.sender.total;
export const getSendersCurrentPage = (state) => state.sender.currentPage;
export const getSendersTotalPages = (state) => state.sender.totalPages;

export const fetchSendersAction =
    ({ queryParams, onSuccess, onFailure } = {}) =>
        async (dispatch) => {
            try {
                let query = transformFiltersToQuery(queryParams);
                dispatch(start());
                communicationApi
                    .get('senders' + query)
                    .then((response) => {
                        dispatch(setSenders(response.data));
                        onSuccess?.(response.data);
                        return response;
                    })
                    .catch((error) => {
                        dispatch(setError(error));
                        if (onFailure) {
                            onFailure(error);
                        }
                    });
            } catch (e) {
                dispatch(stop());
                return console.error('error', e);
            }
        };

export const addSenderAction =
    ({ senderDto, onSuccess, onFailure }) =>
        async (dispatch) => {
            try {
                dispatch(start());
                communicationApi
                    .post('senders', senderDto)
                    .then((response) => {
                        dispatch(addSender(response.data));
                        onSuccess?.(response.data);
                        return response;
                    })
                    .catch((error) => {
                        dispatch(stop());
                        onFailure?.(error?.response?.data);
                    });
            } catch (e) {
                dispatch(stop());
                return console.error(e.message);
            }
        };

export const updateSenderAction =
    ({ id, senderDto, onSuccess, onFailure }) =>
        async (dispatch) => {
            try {
                dispatch(start());
                communicationApi
                    .put('senders/' + id, senderDto)
                    .then((response) => {
                        dispatch(editSender(response.data));
                        onSuccess?.(response.data);
                        return response;
                    })
                    .catch((error) => {
                        dispatch(stop());
                        onFailure?.();
                    });
            } catch (e) {
                dispatch(stop());
                return console.error('error', e);
            }
        };

export const deleteSenderAction =
    ({ id, onSuccess, onFailure }) =>
        async (dispatch) => {
            try {
                dispatch(startDelete(id));
                communicationApi
                    .delete('senders/' + id)
                    .then((response) => {
                        dispatch(deleteSender({ id }));
                        onSuccess?.();
                        return response;
                    })
                    .catch((error) => {
                        onFailure?.(error);
                    });
            } catch (e) {
                onFailure?.(e);
                return console.error(e.message);
            }
        };

export default senderSlice.reducer;

export const confirmSenderVerificationAction =
    ({ id, onSuccess, onFailure }) =>
        async (dispatch) => {
            try {
                dispatch(start());
                communicationApi
                    .post('senders/' + id + '/confirm-verification')
                    .then((response) => {
                        if (response?.data?.verified) {
                            dispatch(confirmSender({ id }));
                        }
                        dispatch(stop());
                        onSuccess?.(response?.data);
                        return response;
                    })
                    .catch((error) => {
                        dispatch(stop());
                        onFailure?.(error);
                    });
            } catch (e) {
                dispatch(stop());
                return console.error(e.message);
            }
        };
